import React, { useEffect, useState, useCallback } from 'react';
import './WelcomePopup.css';

/**
 * WelcomePopup - Premium glassmorphism welcome popup
 * Displays after successful login when user came from a club QR code
 */
const WelcomePopup = ({
    clubName,
    locationString,
    welcomeMessage,
    onClose,
    autoDismissMs = 6000
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [particles, setParticles] = useState([]);

    // Generate confetti particles
    useEffect(() => {
        const colors = ['#c9a45c', '#c8262c', '#f3eadb', '#b5652b', '#d8b56f', '#e0484e'];
        const newParticles = Array.from({ length: 30 }, (_, i) => ({
            id: i,
            left: Math.random() * 100,
            delay: Math.random() * 2,
            duration: 2 + Math.random() * 2,
            color: colors[Math.floor(Math.random() * colors.length)],
            size: 6 + Math.random() * 8,
        }));
        setParticles(newParticles);
    }, []);

    // Entrance animation
    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    // Handle close with exit animation
    const handleClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            onClose?.();
        }, 300);
    }, [onClose]);

    // Auto-dismiss timer
    useEffect(() => {
        if (autoDismissMs > 0) {
            const timer = setTimeout(() => {
                handleClose();
            }, autoDismissMs);
            return () => clearTimeout(timer);
        }
    }, [autoDismissMs, handleClose]);

    const displayMessage = welcomeMessage || `Welcome to ${clubName}!`;

    return (
        <div
            className={`welcome-popup-overlay ${isVisible ? 'visible' : ''} ${isClosing ? 'closing' : ''}`}
            onClick={handleClose}
        >
            {/* Confetti particles */}
            <div className="confetti-container">
                {particles.map((particle) => (
                    <div
                        key={particle.id}
                        className="confetti-particle"
                        style={{
                            left: `${particle.left}%`,
                            animationDelay: `${particle.delay}s`,
                            animationDuration: `${particle.duration}s`,
                            backgroundColor: particle.color,
                            width: `${particle.size}px`,
                            height: `${particle.size}px`,
                        }}
                    />
                ))}
            </div>

            {/* Main popup card */}
            <div
                className={`welcome-popup-card ${isVisible ? 'visible' : ''} ${isClosing ? 'closing' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Glow effect */}
                <div className="card-glow"></div>

                {/* Close button */}
                <button className="close-button" onClick={handleClose}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>

                {/* Content */}
                <div className="popup-content">
                    {/* Poker icon */}
                    <div className="poker-icon">
                        <div className="poker-chip-animated">
                            <span className="suit">♠</span>
                            <span className="suit delayed">♥</span>
                            <span className="suit delayed-2">♦</span>
                            <span className="suit delayed-3">♣</span>
                        </div>
                    </div>

                    {/* Welcome text */}
                    <h1 className="welcome-title">{displayMessage}</h1>

                    {/* Club name if message is custom */}
                    {welcomeMessage && clubName && (
                        <h2 className="club-name-text">{clubName}</h2>
                    )}

                    {/* Location */}
                    {locationString && (
                        <div className="location-container">
                            <span className="location-pin">📍</span>
                            <span className="location-name">{locationString}</span>
                        </div>
                    )}

                    {/* Decorative line */}
                    <div className="decorative-line">
                        <span className="line-dot"></span>
                        <span className="line"></span>
                        <span className="line-dot"></span>
                    </div>

                    {/* Sub message */}
                    <p className="sub-message">Let the games begin!</p>

                    {/* Enter button */}
                    <button className="enter-button" onClick={handleClose}>
                        <span>Enter</span>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>

                {/* Progress bar for auto-dismiss */}
                {autoDismissMs > 0 && (
                    <div className="auto-dismiss-bar">
                        <div
                            className="auto-dismiss-progress"
                            style={{ animationDuration: `${autoDismissMs}ms` }}
                        ></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WelcomePopup;
